import { from, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
/**
 * Standard HTTP headers for JSON response based requests
 * @public
 */
export const JSON_REQUEST_HEADERS = {
    Accept: 'application/json',
};
/**
 * Represents an error generated by a HTTP Fetch Request
 * @public
 */
export class HttpError extends Error {
    constructor(response) {
        super(`Error during request: ${response.status} ${response.statusText}`);
        this.response = response;
        this.status = response.status;
    }
}
/**
 * Generic HTTP Client based on Observables with cache and cancellation support
 * @remarks The cache will get cleaned up automatically when the client is created
 * @public
 */
export class BaseHttpClientV1 {
    /**
     * Creates a new {@link BaseHttpClientV1}
     * @param fetch - Observable based `fetch` (we recommend `@fecoe/fetch`)
     * @param cacheKey - Global key used to cache the requests
     */
    constructor(fetch, cacheKey) {
        this.fetch = fetch;
        this.cache = caches.delete(cacheKey).then(async () => caches.open(cacheKey));
    }
    /** {@inheritDoc HttpClientV1.getRaw} */
    getRaw(input, init, cached) {
        return this.source(input, init).pipe(map(args => this.injectMethod('GET', ...args)), switchMap(args => (cached ? this.cacheFetch(...args) : this.fetch(...args))), switchMap(async (r) => this.responseHandler(r)));
    }
    /** {@inheritDoc HttpClientV1.putRaw} */
    putRaw(input, init) {
        return this.source(input, init).pipe(map(args => this.injectMethod('PUT', ...args)), switchMap(args => this.fetch(...args)), switchMap(async (r) => this.responseHandler(r)));
    }
    /** {@inheritDoc HttpClientV1.postRaw} */
    postRaw(input, init) {
        return this.source(input, init).pipe(map(args => this.injectMethod('POST', ...args)), switchMap(args => this.fetch(...args)), switchMap(async (r) => this.responseHandler(r)));
    }
    /** {@inheritDoc HttpClientV1.deleteRaw} */
    deleteRaw(input, init) {
        return this.source(input, init).pipe(map(args => this.injectMethod('DELETE', ...args)), switchMap(args => this.fetch(...args)), switchMap(async (r) => this.responseHandler(r)));
    }
    /**
     * Handlers the response of a Fetch request by:
     * - Parsing body with {@link BaseHttpClientV1.jsonBodyHandler}
     * - Throwing the body an {@link HttpError} if `status` higher than `400`
     * @param response - Response to handle
     * @returns - Body of the response
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async responseHandler(response) {
        if (response.status >= 400) {
            throw new HttpError(response);
        }
        const body = await this.jsonBodyHandler(response);
        return body;
    }
    /**
     * Handles the response of a Fetch request by:
     * - Returning empty string is the status is `204` (empty response)
     * - Try to parse the body as JSON
     * - If the response has the header `Content-Type: application/json` and the body is not a valid JSON,
     *  it will throw an error
     * - It throws an error in any other case
     * @param response - Response to handle
     * @returns Body of the response
     */
    async jsonBodyHandler(response) {
        if (response.status === 204) {
            return '';
        }
        try {
            return await response.json();
        }
        catch (error) {
            const shouldBeJson = response.headers.get('Content-Type') === 'application/json';
            if (error instanceof SyntaxError && !shouldBeJson) {
                return response.clone().text();
            }
            throw error;
        }
    }
    /**
     * Performs a fetch request using and populating the cache
     * @param input - RequestInfo to use
     * @param init - RequestInit to use
     * @returns Response of the fetch request
     */
    cacheFetch(input, init) {
        const req = new Request(input, init);
        return from(this.cache).pipe(switchMap(async (cache) => cache.match(req)), switchMap(cachedResponse => (cachedResponse ? of(cachedResponse) : this.fetch(req))), switchMap(async (response) => {
            const cache = await this.cache;
            await cache.put(req, response.clone());
            return response;
        }));
    }
    /**
     * Injects a header into a request
     * @param name - Name of the header
     * @param value - Value of the header
     * @param input - RequestInfo to change
     * @param init - RequestInit to change
     * @returns Set of Request info modified
     */
    injectHeader(name, value, input, init) {
        var _a;
        return [
            input,
            {
                ...(init !== null && init !== void 0 ? init : {}),
                headers: {
                    ...((_a = init === null || init === void 0 ? void 0 : init.headers) !== null && _a !== void 0 ? _a : {}),
                    [name]: value,
                },
            },
        ];
    }
    /**
     * Changes the method of a request
     * @param method - Method to use
     * @param input - RequestInfo to change
     * @param init - RequestInit to change
     * @returns Set of Request info modified
     */
    injectMethod(method, input, init) {
        return [
            input,
            {
                ...(init !== null && init !== void 0 ? init : {}),
                method,
            },
        ];
    }
    /**
     * Changes the URL of a request by adding or replacing the base of it
     * @param base - Full URL of the base to use
     * @param input - RequestInfo to change
     * @param init - RequestInit to change
     * @returns Set of Request info modified
     */
    injectBaseUrl(base, input, init) {
        base = base.endsWith('/') ? base : base + '/';
        if (typeof input === 'string') {
            let pathName = new URL(input, document.location.href).pathname;
            pathName = pathName.substring(1);
            return [base + pathName, init];
        }
        let pathName = new URL(input.url, document.location.href).pathname;
        pathName = pathName.substring(1);
        return [new Request(base + pathName, { ...input, ...init }), undefined];
    }
}
